import {PartUpdatingStatus} from "@buildwithflux/core";
import {interval, Subject, Subscription} from "rxjs";
import {bufferWhen, takeUntil} from "rxjs/operators";

import {AppStore} from "modules/app/state/app";
import type {DocumentSaveService} from "modules/document/effects/DocumentSaveService";
import {writeDocument} from "modules/document/state/actionCreators/documentActions";
import type {DocumentStore} from "modules/document/state/document";
import type {PartStore} from "modules/part/state/part";

/**
 * Service that handles part-related notifications.
 *
 * This service replaces the Redux epic `updatePartResultNotifier` by subscribing to
 * Zustand PartStore changes and showing notifications when part updates complete.
 *
 * Note: The `publishPartResultNotifier` epic functionality has been moved directly into the
 * publishing thunks (publishCurrentDocumentAsPart, publishNewPartVersionForCurrentDocument)
 * since the Redux partVersion slice has been removed.
 *
 * The service subscribes to the Zustand PartStore and batches status changes over 3 seconds
 * before showing notifications.
 */
export interface PartNotificationService {
    /**
     * Disposes of all subscriptions and cleans up resources.
     * Should be called when the service is no longer needed.
     */
    dispose: () => void;
}

export interface PartNotificationServiceDependencies {
    appStore: AppStore;
    partStore: PartStore;
    documentStore: DocumentStore;
    documentSaveService: DocumentSaveService;
}

/**
 * Creates a PartNotificationService instance.
 *
 * This factory function creates a service that handles part updating
 * notifications by subscribing to Zustand PartStore changes.
 *
 * @param dependencies - The service dependencies
 * @returns A PartNotificationService instance
 */
export function createPartNotificationService(
    dependencies: PartNotificationServiceDependencies,
): PartNotificationService {
    const {appStore, partStore, documentStore, documentSaveService} = dependencies;
    const statusChange$ = new Subject<{partUid: string; status: PartUpdatingStatus}>();
    const destroy$ = new Subject<void>();
    const subscriptions: Subscription[] = [];
    let disposed = false;

    // Track previous state to detect changes
    let previousPartUpdatingMap: Record<string, PartUpdatingStatus> = {};

    // Subscribe to Zustand PartStore changes
    const unsubscribeFromStore = partStore.subscribe((state) => {
        if (disposed) return;

        const currentMap = state.partUpdatingMap;

        // Detect changes in partUpdatingMap
        for (const [partUid, status] of Object.entries(currentMap)) {
            const previousStatus = previousPartUpdatingMap[partUid];
            // Only emit if status changed to a terminal state (success, failure, denied)
            if (
                previousStatus !== status &&
                (status === PartUpdatingStatus.success ||
                    status === PartUpdatingStatus.failure ||
                    status === PartUpdatingStatus.denied)
            ) {
                statusChange$.next({partUid, status});
            }
        }

        previousPartUpdatingMap = {...currentMap};
    });

    // Update part result notifier
    // Handles notifications when part updating completes, batching results over 3 seconds
    const updateSubscription = statusChange$
        .pipe(
            // Use takeUntil(destroy$) FIRST to ensure the entire pipeline is cancelled
            // when dispose() is called. This is critical because bufferWhen schedules
            // timers that could fire after unsubscribe() if not properly cancelled.
            takeUntil(destroy$),
            bufferWhen(() => interval(3000)),
        )
        .subscribe((bufferedChanges) => {
            if (bufferedChanges.length === 0) return;

            let failures = 0;
            let successes = 0;
            let denieds = 0;

            for (const change of bufferedChanges) {
                if (change.status === PartUpdatingStatus.failure) failures += 1;
                else if (change.status === PartUpdatingStatus.success) successes += 1;
                else if (change.status === PartUpdatingStatus.denied) denieds += 1;
            }

            // Handle success notifications and dispatch writeDocument to save
            if (successes > 0) {
                const messageObject = successes > 1 ? `${successes} components` : "component";
                const message = `Successfully updated ${messageObject}.`;
                appStore.getState().enqueueNotification(message, "part", {
                    variant: "success",
                    persist: false,
                    preventDuplicate: true,
                    autoHideDuration: 5000,
                });
                // Dispatch writeDocument action to save the document to Firebase
                const documentData = documentStore.getState().document;
                if (documentData) {
                    const action = writeDocument(documentData, false);
                    documentSaveService.onAction(action);
                }
            }

            // Handle failure notifications
            if (failures > 0) {
                const messageObject = failures > 1 ? `${failures} components` : "component";
                const message = `Failed to update ${messageObject}. Please try again in a moment.`;
                appStore.getState().enqueueNotification(message, "part", {
                    variant: "error",
                    persist: false,
                    preventDuplicate: true,
                    autoHideDuration: 5000,
                });
            }

            // Handle denied notifications
            if (denieds > 0) {
                const messageObject = denieds > 1 ? `${denieds} components` : "component";
                const message = `Failed to update ${messageObject}. Permission denied.`;
                appStore.getState().enqueueNotification(message, "part", {
                    variant: "error",
                    persist: false,
                    preventDuplicate: true,
                    autoHideDuration: 5000,
                });
            }
        });
    subscriptions.push(updateSubscription);

    const service: PartNotificationService = {
        dispose: () => {
            // Mark as disposed first to prevent any new changes from being processed
            disposed = true;

            // Unsubscribe from Zustand store
            unsubscribeFromStore();

            // Emit on destroy$ to cancel the RxJS pipeline (including any pending timers)
            // This is more reliable than just calling unsubscribe() because takeUntil
            // will complete the stream synchronously, cancelling any scheduled timers
            destroy$.next();
            destroy$.complete();

            // Also unsubscribe for completeness
            for (const subscription of subscriptions) {
                subscription.unsubscribe();
            }
            statusChange$.complete();
        },
    };

    return service;
}
